import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { Building2, Plus, Search, MoreHorizontal, Eye, Trash2, Users, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { useSortableTable } from "@/hooks/useSortableTable";
import { SortableHead } from "@/components/SortableHead";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Pagination, paginate } from "@/components/Pagination";

const STATUS_STYLES = {
  draft: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  closed: "bg-red-500/10 text-red-400 border-red-500/20",
};

export default function FundManagement() {
  const [funds, setFunds] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const navigate = useNavigate();

  const loadFunds = useCallback(async () => {
    try {
      const { data } = await API.get("/funds");
      setFunds(data);
    } catch { toast.error("Failed to load funds"); }
    finally { setLoading(false); }
  }, []);

  const loadOrgs = useCallback(async () => {
    try {
      const { data } = await API.get("/organizations");
      setOrgs(data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadFunds(); loadOrgs(); }, [loadFunds, loadOrgs]);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const deleteFund = async (id) => {
    try {
      await API.delete(`/funds/${id}`);
      toast.success("Fund deleted");
      loadFunds();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const orgMap = Object.fromEntries(orgs.map(o => [o.id, o.name]));

  const filtered = funds.filter((f) => {
    // Trim leading/trailing whitespace before matching.
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const orgName = orgMap[f.org_id] || "";
    return f.name?.toLowerCase().includes(q) || f.fund_type?.toLowerCase().includes(q) || orgName.toLowerCase().includes(q);
  });

  const { sorted: sortedFunds, sortKey, sortDir, toggleSort } = useSortableTable(filtered);
  const { paginatedItems: pagedFunds, totalPages: fundsTotalPages, safePage: fundsSafePage } = paginate(sortedFunds, page, pageSize);

  const stats = [
    { label: "Total Funds", value: funds.length, color: "text-blue-500 dark:text-blue-400", bg: "bg-blue-500/10 border-blue-500/20" },
    { label: "Active", value: funds.filter((f) => f.status === "active").length, color: "text-emerald-500 dark:text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
    { label: "Draft", value: funds.filter((f) => f.status === "draft").length, color: "text-amber-500 dark:text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="fund-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Fund Management</h1>
          <p className="text-muted-foreground text-sm mt-1">Create and manage investment funds via PPM upload</p>
        </div>
        <Button asChild className="shadow-[0_0_20px_rgba(108,71,196,0.2)]" data-testid="create-fund-button" disabled={orgs.length === 0}>
          <Link to={orgs.length === 0 ? "#" : "/super-admin/funds/create"} onClick={(e) => { if (orgs.length === 0) { e.preventDefault(); toast.error("Please create an Organization before adding Funds"); } }}>
            <Plus className="w-4 h-4 mr-2" /> Create Fund
          </Link>
        </Button>
      </div>

      {orgs.length === 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-400" data-testid="no-orgs-warning">
          Please create an <strong>Organization</strong> before adding Funds. Head over to{" "}
          <Link to="/super-admin/organizations" className="underline font-medium">Organizations</Link> to create one.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {stats.map((s) => (
          <Card key={s.label} className={`stat-card ${s.bg}`}>
            <CardContent className="p-5">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{s.label}</span>
              <p className={`font-heading text-2xl font-bold mt-1 ${s.color}`}>
                {loading ? <span className="inline-block w-8 h-6 bg-secondary rounded animate-pulse" /> : s.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card data-testid="funds-table-card">
        <div className="p-6 pb-4">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search funds..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-secondary/50 h-10" data-testid="fund-search-input" />
          </div>
        </div>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                <SortableHead sortKey="name" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold pl-6">Fund Name</SortableHead>
                <SortableHead sortKey="org_id" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Organization</SortableHead>
                <SortableHead sortKey="fund_type" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Type</SortableHead>
                <SortableHead sortKey="reporting_frequency" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Reporting</SortableHead>
                <SortableHead sortKey="status" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Status</SortableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Managers</TableHead>
                <SortableHead sortKey="created_at" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Created</SortableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Radar</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Recon</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold text-right pr-6">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [1, 2, 3].map((i) => (
                  <TableRow key={i}><TableCell colSpan={10} className="pl-6"><div className="h-10 bg-secondary/30 rounded animate-pulse" /></TableCell></TableRow>
                ))
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-12">
                  <Building2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" strokeWidth={1} />
                  <p className="text-muted-foreground text-sm">{search ? "No funds match" : "No funds yet. Upload a PPM to get started."}</p>
                </TableCell></TableRow>
              ) : pagedFunds.map((f) => (
                <TableRow key={f.id} className="table-row-interactive cursor-pointer" onClick={() => navigate(`/super-admin/funds/${f.id}`)} data-testid={`fund-row-${f.id}`}>
                  <TableCell className="pl-6 font-medium">{f.name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{orgMap[f.org_id] || "—"}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{f.fund_type || "—"}</TableCell>
                  <TableCell className="text-sm capitalize">{f.reporting_frequency || "—"}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${STATUS_STYLES[f.status]}`}>{f.status}</Badge></TableCell>
                  <TableCell><span className="flex items-center gap-1 text-sm text-muted-foreground"><Users className="w-3.5 h-3.5" />{f.admin_ids?.length || 0}</span></TableCell>
                  <TableCell className="text-muted-foreground text-sm">{f.created_at ? format(new Date(f.created_at), "MMMM d, yyyy") : "—"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${
                      f.radar_sync_status === "success" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" :
                      f.radar_sync_status === "failed" ? "bg-red-500/10 text-red-400 border-red-500/20" :
                      "bg-amber-500/10 text-amber-400 border-amber-500/20"
                    }`}>
                      {f.radar_sync_status || "pending"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`text-[10px] uppercase tracking-wider ${
                        f.recon_sync_status === "synced" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" :
                        f.recon_sync_status === "failed" ? "bg-red-500/10 text-red-400 border-red-500/20" :
                        f.recon_sync_status === "skipped" ? "bg-slate-500/10 text-slate-400 border-slate-500/20" :
                        "bg-amber-500/10 text-amber-400 border-amber-500/20"
                      }`}
                      title={f.recon_sync_status === "failed" ? (f.recon_sync_error || "") : (f.recon_sync_status === "skipped" ? (f.recon_sync_error || "Not required") : "")}
                      data-testid={`fund-recon-status-${f.id}`}
                    >
                      {(() => {
                        switch ((f.recon_sync_status || "").toLowerCase()) {
                          case "synced":   return "Synced";
                          case "failed":   return "Failed";
                          case "skipped":  return "Not Required";
                          default:         return "Pending";
                        }
                      })()}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right pr-6" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="w-8 h-8"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-card border-border">
                        <DropdownMenuItem onClick={() => navigate(`/super-admin/funds/${f.id}`)}><Eye className="w-4 h-4 mr-2" /> View</DropdownMenuItem>
                        {(f.radar_sync_status === "failed" || f.radar_sync_status === "pending") && (
                          <DropdownMenuItem onClick={async (e) => { e.stopPropagation(); try { await API.post(`/funds/${f.id}/sync-radar`); toast.success("Radar sync queued"); setTimeout(loadFunds, 2000); } catch { toast.error("Failed"); } }}><RefreshCw className="w-4 h-4 mr-2" /> Retry Radar sync</DropdownMenuItem>
                        )}
                        {f.status === "draft" && (
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setConfirmDelete(f); }} className="text-red-400 focus:text-red-400"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!loading && sortedFunds.length > 0 && (
            <div className="px-6 pb-4">
              <Pagination currentPage={fundsSafePage} totalPages={fundsTotalPages} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={(v) => { setPageSize(v); setPage(1); }} />
            </div>
          )}
        </CardContent>
      </Card>
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        title="Delete Fund"
        description={`Are you sure you want to delete "${confirmDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete Fund"
        onConfirm={() => deleteFund(confirmDelete?.id)}
      />
    </div>
  );
}
